import React, { useEffect } from 'react';
import { SubPageTemplate } from '../../partials';
import { AssurancePolicy, POLICY_TYPES, Props } from './LifeAssurance.types';
import validationSchema from './validationSchema';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { Box, Columns, Icon } from 'modules/af_cm_ui_kit/components';
import { useDispatch } from 'react-redux';
import css from './LifeAssurance.module.scss';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import useCaseIsReadOnly from 'helpers/hooks/useCaseIsReadOnly/useCaseIsReadOnly';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../form/yup/reactHookFormResolver';
import { assurancePoliciesApi } from '../../../../api';
import { updateLifeAssurance } from '../../../../store/cases/update/actions';
import { saveCase, setErrorModal } from '../../../../store/cases/actions';
import useSaveButtonClicked from '../../hooks/useSaveButtonClicked';
import * as subPages from '../../../../store/subPages';
import { SUBPAGES } from '../../../../store/subPages/types';
import CheckDirty from '../../../../components/CheckDirty/CheckDirty';

type FormType = {
  [FIELDS.ASSURANCE_POLICIES_PREFIX]: Omit<AssurancePolicy, 'id'>[];
};

const LifeAssurance: React.FC<Props> = ({ data, initialData, resources, caseId }) => {
  const defaultValues = { [FIELDS.ASSURANCE_POLICIES_PREFIX]: initialData };
  const isCaseReadOnly = useCaseIsReadOnly();
  const dispatch = useDispatch();
  const methods = useForm<FormType>({
    mode: 'onBlur',
    defaultValues,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { control, reset } = methods;

  useEffect(() => {
    reset(
      { [FIELDS.ASSURANCE_POLICIES_PREFIX]: data },
      {
        keepDefaultValues: true,
      },
    );
  }, [data, reset]);

  const { fields: lifeAssurancePolicies, append, remove } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.ASSURANCE_POLICIES_PREFIX,
  });

  useEffect(() => {
    return () => {
      dispatch(updateLifeAssurance(methods.getValues()[FIELDS.ASSURANCE_POLICIES_PREFIX]));
    };
  }, [dispatch, methods]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateLifeAssurance(methods.getValues()[FIELDS.ASSURANCE_POLICIES_PREFIX]));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateLifeAssurance(methods.getValues()[FIELDS.ASSURANCE_POLICIES_PREFIX]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getErrors } = usePepperError('loanDetails');

  const handleAddAssuranceClick = () => {
    assurancePoliciesApi(caseId)
      .post()
      .then((res) => {
        append(res.data);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const handleDeletePolicy = (assurancePolicy: AssurancePolicy, index: number) => {
    assurancePoliciesApi(caseId)
      .remove(assurancePolicy.id)
      .then(() => {
        remove(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };
  return (
    <SubPageTemplate title="Life assurance">
      <FormProvider<FormType> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(subPages.actions.updateSubPageIsDirty(SUBPAGES.LIFE_ASSURANCE, isDirty));
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <Box title="Life assurance">
              {lifeAssurancePolicies.map((field, i) => {
                const assurancePolicy = methods.getValues()[FIELDS.ASSURANCE_POLICIES_PREFIX][i] as AssurancePolicy;

                if (!assurancePolicy) {
                  return <></>;
                }

                return (
                  <div className={css.wrapper} key={field.key}>
                    <div className={css.actionsRow}>
                      <div className={css.content}>
                        <UpdatableField
                          getErrors={getErrors}
                          fieldType={INPUTS.SELECT}
                          field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_ASSURED_PERSON]}
                          options={resources.assuredPersons}
                          required
                        />
                      </div>
                      <div className={css.actions}>
                        <button
                          type="button"
                          className={css.button}
                          onClick={() => handleDeletePolicy(assurancePolicy, i)}
                          disabled={isCaseReadOnly}>
                          <Icon type="trash" />
                        </button>
                      </div>
                    </div>
                    <UpdatableField
                      getErrors={getErrors}
                      fieldType={INPUTS.SELECT}
                      field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_TYPE]}
                      options={resources.assurancePolicyTypes}
                      required
                    />
                    {[
                      POLICY_TYPES.DECREASING_TERM_ASSURANCE,
                      POLICY_TYPES.LEVEL_TERM_ASSURANCE,
                      POLICY_TYPES.FAMILY_INCOME_BENEFIT,
                    ].includes((assurancePolicy[FIELDS.ASSURANCE_POLICY_TYPE] as POLICY_TYPES) || '') && (
                      <UpdatableField
                        getErrors={getErrors}
                        fieldType={INPUTS.TEXT}
                        field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_EXPIRY_DATE]}
                        variant={VARIANTS_TYPES.DATE}
                      />
                    )}

                    {[POLICY_TYPES.FAMILY_INCOME_BENEFIT].includes(
                      (assurancePolicy[FIELDS.ASSURANCE_POLICY_TYPE] as POLICY_TYPES) || '',
                    ) && (
                      <UpdatableField
                        getErrors={getErrors}
                        fieldType={INPUTS.TEXT}
                        field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_PAYMENTS_PER_ANNUM]}
                        variant={VARIANTS_TYPES.CURRENCY}
                      />
                    )}

                    {[POLICY_TYPES.WHOLE_OF_LIFE].includes(
                      (assurancePolicy[FIELDS.ASSURANCE_POLICY_TYPE] as POLICY_TYPES) || '',
                    ) && (
                      <UpdatableField
                        getErrors={getErrors}
                        fieldType={INPUTS.SELECT}
                        field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_BASIS_OF_COVER]}
                        options={resources.basisCovers}
                        required
                      />
                    )}

                    <UpdatableField
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_INSURANCE_LUMP_SUM]}
                      variant={VARIANTS_TYPES.CURRENCY}
                    />

                    <UpdatableField
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      field={fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_MONTHLY_PAYMENT]}
                      variant={VARIANTS_TYPES.CURRENCY}
                    />

                    <UpdatableField
                      getErrors={getErrors}
                      fieldType={INPUTS.TEXT}
                      field={
                        fields[FIELDS.ASSURANCE_POLICIES_PREFIX](i)[FIELDS.ASSURANCE_POLICY_MORTGAGE_MONTHLY_PAYMENTS]
                      }
                      variant={VARIANTS_TYPES.CURRENCY}
                    />
                    <hr className={css.divider} />
                  </div>
                );
              })}
              <hr className={css.divider} />
              <button
                type="button"
                className={css.button}
                onClick={handleAddAssuranceClick}
                disabled={isCaseReadOnly}
                style={{ marginTop: 32, marginBottom: -16 }}>
                Add {methods.getValues()[FIELDS.ASSURANCE_POLICIES_PREFIX].length ? 'another' : ''} policy
              </button>
            </Box>
            <></>
          </Columns>
        </form>
      </FormProvider>
    </SubPageTemplate>
  );
};

export default LifeAssurance;
